/* eslint-disable import/prefer-default-export */
import { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import ShippingCostModel from '../../../models/ShippingCostModelMC';
import { formatMoney } from '../../../../util/numberUtils';

/**
 * Hook para cargar y gestionar los costos de envío
 * Responsabilidad única: Fetch y manejo de opciones de costos de envío
 * Los datos se cachean en Redux para evitar recargas innecesarias entre navegaciones
 */
export const useShippingCosts = (shippingCostMethod) => {
  const dispatch = useDispatch();

  const { retailer_info } = useSelector(
    (state) => state.retailerUsersReducer
  );

  const { currency_code, currency_symbol } = retailer_info;

  const [shippingCostOptions, setShippingCostOptions] = useState([]);
  const [loadingShippingCosts, setLoadingShippingCosts] = useState(false);
  const [error, setError] = useState(null);

  // Caché en Redux para evitar recargas entre navegaciones
  const shippingCostsCache = useSelector(
    (state) => state.ordersReducer?.shippingCostsCache || {}
  );

  // Memoizar la clave de caché para evitar recálculos innecesarios
  const cacheKey = useMemo(() => `method_${shippingCostMethod}`, [shippingCostMethod]);

  // Memoizar si ya existe en caché
  const cachedOptions = useMemo(
    () => shippingCostsCache[cacheKey],
    [shippingCostsCache, cacheKey]
  );

  useEffect(() => {
    if (!shippingCostMethod) {
      setShippingCostOptions([]);
      return;
    }

    // Si ya tenemos los datos en caché, usarlos
    if (cachedOptions) {
      setShippingCostOptions(cachedOptions);
      setLoadingShippingCosts(false);
      return;
    }

    // Si no está en caché, hacer el fetch
    const fetchShippingCosts = async () => {
      setLoadingShippingCosts(true);
      setError(null);

      try {
        const response = await ShippingCostModel.get({
          page: 1,
          'q[shipping_type]': shippingCostMethod,
          'q[active]': true
        });

        const options = (response.shipping_costs || []).map((item) => ({
          id: String(item.id),
          value: item.id,
          label:
            item.shipping_type === 'location_cost'
              ? `${item.location_name} - ${currency_symbol} ${formatMoney(item.cost)} ${currency_code}`
              : `${currency_symbol} ${formatMoney(item.cost)} ${currency_code}`,
          cost: item.cost,
          shipping_type: item.shipping_type
        }));

        options.push({
          id: 'custom',
          value: 'custom',
          label: 'Personalizado',
          cost: ''
        });

        setShippingCostOptions(options);

        // Guardar en Redux para persistencia entre navegaciones
        dispatch({
          type: 'CACHE_SHIPPING_COSTS',
          payload: {
            key: cacheKey,
            options
          }
        });
      } catch (err) {
        console.error('Error fetching shipping costs:', err);
        setError(err);
        setShippingCostOptions([]);
      } finally {
        setLoadingShippingCosts(false);
      }
    };

    fetchShippingCosts();
  }, [shippingCostMethod, cacheKey, cachedOptions, currency_symbol, currency_code, dispatch]);

  return {
    shippingCostOptions,
    loadingShippingCosts,
    error,
    isEmpty: shippingCostOptions.length === 0
  };
};
